import React, { useState } from 'react';
import { collection, getDocs, doc, updateDoc, deleteField, FieldPath } from 'firebase/firestore';
import { db } from '../../../config/firebase';

interface MigrationPreview {
  camperId: string;
  firstName: string;
  lastName: string;
  email: string;
  oldData: { [key: string]: any };
  newData: { year: string; placeCode: string }[];
}

const AccommodationHistoryMigration: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<MigrationPreview[]>([]);
  const [stats, setStats] = useState({ total: 0, needsMigration: 0, alreadyMigrated: 0, canCleanup: 0 });
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState<{ success: number; errors: number; messages: string[] }>({
    success: 0,
    errors: 0,
    messages: []
  });

  const checkMigrationStatus = async () => {
    setLoading(true);
    try {
      const primaryCampersRef = collection(db, 'primaryCampers');
      const snapshot = await getDocs(primaryCampersRef);
      
      let needsMigration = 0;
      let alreadyMigrated = 0;
      let canCleanup = 0;

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const hasNewFormat = data.accommodationHistory && Array.isArray(data.accommodationHistory);
        
        // Check if has old format data
        let hasOldData = false;
        for (let i = 1; i <= 8; i++) {
          if (data[`Acc.${i}`] || data[`Acc${i.toString().padStart(2, '0')}`]) {
            hasOldData = true;
            break;
          }
        }
        
        if (hasNewFormat) {
          alreadyMigrated++;
          if (hasOldData) {
            canCleanup++;
          }
        } else if (hasOldData) {
          needsMigration++;
        }
      });

      setStats({
        total: snapshot.size,
        needsMigration,
        alreadyMigrated,
        canCleanup
      });
    } catch (error) {
      console.error('Error checking migration status:', error);
      alert('Error checking migration status: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const previewMigration = async () => {
    setLoading(true);
    setPreviewing(true);
    setPreview([]);

    try {
      const primaryCampersRef = collection(db, 'primaryCampers');
      const snapshot = await getDocs(primaryCampersRef);
      
      const previewData: MigrationPreview[] = [];

      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        
        // Skip if already migrated
        if (data.accommodationHistory && Array.isArray(data.accommodationHistory)) {
          return;
        }

        const oldData: { [key: string]: any } = {};
        const newData: { year: string; placeCode: string }[] = [];

        for (let i = 1; i <= 8; i++) {
          const yearField = `Acc.${i}`;
          const codeField = `Acc${i.toString().padStart(2, '0')}`;
          
          const year = data[yearField];
          const placeCode = data[codeField];

          if (year || placeCode) {
            oldData[yearField] = year;
            oldData[codeField] = placeCode;
          }

          if (year) {
            newData.push({
              year: String(year),
              placeCode: placeCode ? String(placeCode) : 'No History'
            });
          }
        }

        if (Object.keys(oldData).length > 0) {
          // Sort by year descending
          newData.sort((a, b) => parseInt(b.year) - parseInt(a.year));

          previewData.push({
            camperId: docSnap.id,
            firstName: data.FirstName || '',
            lastName: data.LastName || '',
            email: data.email || '',
            oldData,
            newData
          });
        }
      });

      setPreview(previewData);
    } catch (error) {
      console.error('Error previewing migration:', error);
      alert('Error previewing migration: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const executeMigration = async () => {
    if (!window.confirm(`This will migrate ${stats.needsMigration} records. Are you sure you want to proceed?`)) {
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: stats.needsMigration });
    setResults({ success: 0, errors: 0, messages: [] });

    try {
      const primaryCampersRef = collection(db, 'primaryCampers');
      const snapshot = await getDocs(primaryCampersRef);
      
      let current = 0;
      let success = 0;
      let errors = 0;
      const messages: string[] = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        
        // Skip if already migrated
        if (data.accommodationHistory && Array.isArray(data.accommodationHistory)) {
          continue;
        }

        const accommodationHistory: { year: string; placeCode: string }[] = [];

        for (let i = 1; i <= 8; i++) {
          const yearField = `Acc.${i}`;
          const codeField = `Acc${i.toString().padStart(2, '0')}`;
          
          const year = data[yearField];
          const placeCode = data[codeField];

          if (year) {
            accommodationHistory.push({
              year: String(year),
              placeCode: placeCode ? String(placeCode) : 'No History'
            });
          }
        }

        if (accommodationHistory.length > 0) {
          try {
            // Sort by year descending
            accommodationHistory.sort((a, b) => parseInt(b.year) - parseInt(a.year));

            // Update the document
            const docRef = doc(db, 'primaryCampers', docSnap.id);
            await updateDoc(docRef, {
              accommodationHistory
            });

            success++;
            messages.push(`✓ Migrated ${data.FirstName} ${data.LastName} (${accommodationHistory.length} entries)`);
          } catch (error) {
            errors++;
            messages.push(`✗ Error migrating ${data.FirstName} ${data.LastName}: ${(error as Error).message}`);
          }

          current++;
          setProgress({ current, total: stats.needsMigration });
          setResults({ success, errors, messages: [...messages] });
        }
      }

      // Refresh stats
      await checkMigrationStatus();
      
      alert(`Migration complete!\nSuccess: ${success}\nErrors: ${errors}`);
    } catch (error) {
      console.error('Error executing migration:', error);
      alert('Error executing migration: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const cleanupOldFields = async () => {
    if (!window.confirm(
      `This will remove old Acc.1-Acc.8 and Acc01-Acc08 fields from ${stats.canCleanup} migrated records. This cannot be undone. Are you sure?`
    )) {
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: stats.canCleanup });
    setResults({ success: 0, errors: 0, messages: [] });

    try {
      const primaryCampersRef = collection(db, 'primaryCampers');
      const snapshot = await getDocs(primaryCampersRef);
      
      let current = 0;
      let success = 0;
      let errors = 0;
      let skipped = 0;
      const messages: string[] = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        
        // Health Check: Only cleanup if has new format array with actual data
        const hasNewFormat = data.accommodationHistory && Array.isArray(data.accommodationHistory);
        const hasArrayData = hasNewFormat && data.accommodationHistory.length > 0;
        
        if (!hasArrayData) {
          // Skip this record - no valid array to fall back on
          continue;
        }

        try {
          // Final health check: Verify array still exists before deletion
          const arrayLength = data.accommodationHistory.length;
          
          const docRef = doc(db, 'primaryCampers', docSnap.id);
          
          // Delete simple fields (Acc01-Acc08)
          await updateDoc(docRef, {
            'Acc01': deleteField(),
            'Acc02': deleteField(),
            'Acc03': deleteField(),
            'Acc04': deleteField(),
            'Acc05': deleteField(),
            'Acc06': deleteField(),
            'Acc07': deleteField(),
            'Acc08': deleteField(),
            'Acc': deleteField()
          });
          
          // Delete fields with dots in their names (Acc.1-Acc.8)
          // Must pass as individual arguments, not in an object
          await updateDoc(docRef,
            new FieldPath('Acc.1'), deleteField(),
            new FieldPath('Acc.2'), deleteField(),
            new FieldPath('Acc.3'), deleteField(),
            new FieldPath('Acc.4'), deleteField(),
            new FieldPath('Acc.5'), deleteField(),
            new FieldPath('Acc.6'), deleteField(),
            new FieldPath('Acc.7'), deleteField(),
            new FieldPath('Acc.8'), deleteField()
          );

          success++;
          messages.push(`✓ Cleaned up ${data.FirstName} ${data.LastName} (${arrayLength} history entries preserved)`);
        } catch (error) {
          errors++;
          messages.push(`✗ Error cleaning ${data.FirstName} ${data.LastName}: ${(error as Error).message}`);
        }

        current++;
        setProgress({ current, total: stats.canCleanup });
        setResults({ success, errors, messages: [...messages] });
      }

      // Refresh stats
      await checkMigrationStatus();
      
      const message = `Cleanup complete!\nSuccess: ${success}\nErrors: ${errors}${skipped > 0 ? `\nSkipped (no array): ${skipped}` : ''}`;
      alert(message);
      if (skipped > 0) {
        messages.push(`\n⚠️ Skipped ${skipped} records that had no accommodationHistory array`);
        setResults(prev => ({ ...prev, messages: [...messages] }));
      }
    } catch (error) {
      console.error('Error cleaning up old fields:', error);
      alert('Error cleaning up old fields: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section>
      <div className="action-section">
        <div className="action-header">
          <h3>Migrate Accommodation History to Array</h3>
          <p>Converts accommodation history from Acc.1 Acc01 to array.</p>
        </div>

        {/* Stats Display */}
        {stats.total > 0 && (
          <div style={{ 
            padding: '1rem', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '4px', 
            marginBottom: '1rem',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <div>
                <strong>Total Records:</strong> {stats.total}
              </div>
              <div>
                <strong style={{ color: '#dc3545' }}>Needs Migration:</strong> {stats.needsMigration}
              </div>
              <div>
                <strong style={{ color: '#28a745' }}>Already Migrated:</strong> {stats.alreadyMigrated}
              </div>
              <div>
                <strong style={{ color: '#ffc107' }}>Can Clean Up Old Fields:</strong> {stats.canCleanup}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            onClick={checkMigrationStatus}
            disabled={loading}
            className="action-button"
            style={{ flex: 1 }}
          >
            {loading && !previewing ? 'Checking...' : 'Check Status'}
          </button>
          
          <button
            onClick={previewMigration}
            disabled={loading || stats.needsMigration === 0}
            className="action-button"
            style={{ flex: 1, backgroundColor: '#007bff' }}
          >
            {previewing && loading ? 'Loading Preview...' : 'Preview Migration'}
          </button>
          
          <button
            onClick={executeMigration}
            disabled={loading || stats.needsMigration === 0}
            className="action-button"
            style={{ flex: 1, backgroundColor: '#28a745' }}
          >
            {loading && !previewing ? 'Executing...' : 'Execute Migration'}
          </button>
          
          <button
            onClick={cleanupOldFields}
            disabled={loading || stats.canCleanup === 0}
            className="action-button"
            style={{ flex: 1, backgroundColor: '#dc3545' }}
          >
            {loading && !previewing ? 'Cleaning...' : 'Clean Up Old Fields'}
          </button>
        </div>

        {/* Progress Display */}
        {progress.total > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: '0.5rem',
              fontSize: '0.9rem'
            }}>
              <span>Progress: {progress.current} / {progress.total}</span>
              <span>{Math.round((progress.current / progress.total) * 100)}%</span>
            </div>
            <div style={{ 
              width: '100%', 
              height: '20px', 
              backgroundColor: '#e9ecef', 
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{ 
                width: `${(progress.current / progress.total) * 100}%`, 
                height: '100%', 
                backgroundColor: '#28a745',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>
        )}

        {/* Results Display */}
        {results.messages.length > 0 && (
          <div style={{ 
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            border: '1px solid #dee2e6',
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            <div style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Results: {results.success} successful, {results.errors} errors
            </div>
            <div style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
              {results.messages.map((msg, idx) => (
                <div key={idx} style={{ 
                  color: msg.startsWith('✓') ? '#28a745' : '#dc3545',
                  marginBottom: '0.25rem'
                }}>
                  {msg}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preview Display */}
        {preview.length > 0 && (
          <div style={{ 
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#fff3cd',
            borderRadius: '4px',
            border: '1px solid #ffc107',
            maxHeight: '500px',
            overflowY: 'auto'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '1rem' }}>
              Preview: {preview.length} records will be migrated
            </h4>
            
            {preview.slice(0, 10).map((item, idx) => (
              <div key={idx} style={{ 
                marginBottom: '1rem',
                padding: '0.75rem',
                backgroundColor: 'white',
                borderRadius: '4px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  {item.firstName} {item.lastName} ({item.email})
                </div>
                
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem' }}>
                  <div style={{ flex: 1 }}>
                    <strong>Old Format:</strong>
                    <pre style={{ 
                      margin: '0.25rem 0 0 0', 
                      padding: '0.5rem',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(item.oldData, null, 2)}
                    </pre>
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <strong>New Format:</strong>
                    <pre style={{ 
                      margin: '0.25rem 0 0 0', 
                      padding: '0.5rem',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(item.newData, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            ))}
            
            {preview.length > 10 && (
              <div style={{ 
                textAlign: 'center', 
                fontStyle: 'italic',
                color: '#6c757d',
                marginTop: '1rem'
              }}>
                Showing first 10 of {preview.length} records
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default AccommodationHistoryMigration;

